/**
 * RestraintSetCard — surfaces when the cascade engine flags
 * 'restraint_use' (chemical / physical restraint applied).
 *
 * Captures justification, restraint types, online-MD contact, and the
 * downstream Serious / Unusual Occurrence trigger.
 */

import type { EPCR } from '@/types';
import { SetCard, LabeledRow, ChipSelect } from './SetCard';

interface Props {
  epcr: EPCR;
  onUpdate: (updates: Partial<EPCR>) => void;
}

type RestraintFindings = {
  types?: string[];           // soft, hard 4-point, chemical, spit hood
  justification?: string;
  md_contact_name?: string;
  md_contact_time?: string;
  pre_post_vitals?: 'documented' | 'attempted' | 'unable';
};

const TYPES = ['soft 4-point', 'hard 4-point', 'chemical', 'spit hood', 'other'];

export function RestraintSetCard({ epcr, onUpdate }: Props) {
  const f = ((epcr.set_findings ?? {}).restraint_use ?? {}) as RestraintFindings;
  const update = (patch: Partial<RestraintFindings>) => {
    onUpdate({
      set_findings: {
        ...(epcr.set_findings ?? {}),
        restraint_use: { ...f, ...patch },
      },
    });
  };
  const locked = epcr.locked;

  return (
    <SetCard
      title="Restraint Use Set"
      triggeredBy="Restraint applied. RI Protocol + departmental policy require justification, MD contact, and a Serious / Unusual Occurrence Report."
      feeds={['nemsis_xml', 'serious_unusual', 'narrative_engine']}
      accent="rose"
    >
      <LabeledRow label="Restraint Type(s)">
        <ChipSelect
          options={TYPES}
          value={f.types ?? []}
          disabled={locked}
          multi
          onChange={(v) => update({ types: v as string[] })}
        />
      </LabeledRow>

      <LabeledRow label="Justification" hint="Why was restraint indicated? Behavioral, danger to self/others, etc.">
        <textarea
          disabled={locked}
          value={f.justification ?? ''}
          onChange={(e) => update({ justification: e.target.value })}
          className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
          rows={3}
        />
      </LabeledRow>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <LabeledRow label="Online-MD Contact (name)">
          <input
            type="text"
            disabled={locked}
            value={f.md_contact_name ?? ''}
            onChange={(e) => update({ md_contact_name: e.target.value })}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm min-h-touch focus:border-blue-500 focus:outline-none"
          />
        </LabeledRow>
        <LabeledRow label="MD Contact Time">
          <input
            type="datetime-local"
            disabled={locked}
            value={f.md_contact_time ?? ''}
            onChange={(e) => update({ md_contact_time: e.target.value })}
            className="rounded border border-slate-300 px-3 py-2 text-sm min-h-touch focus:border-blue-500 focus:outline-none"
          />
        </LabeledRow>
      </div>

      <LabeledRow label="Pre + Post-Restraint Vitals" hint="Required for chemical restraints.">
        <ChipSelect
          options={['documented', 'attempted', 'unable']}
          value={f.pre_post_vitals ?? null}
          disabled={locked}
          onChange={(v) => update({ pre_post_vitals: v as RestraintFindings['pre_post_vitals'] })}
        />
      </LabeledRow>
    </SetCard>
  );
}
